import React from "react";
import { X, User, Calendar, ArrowRight } from "lucide-react";
import { ISalesRepAssigneeLog } from "../../models/epcCustomer.model";
import { formatDate } from "../../utils/formatDateUtils";
import Button, { ButtonType } from "../Assets/Button";

interface ViewAssigneeLogsModalProps {
  isOpen: boolean;
  onClose: () => void;
  logs: ISalesRepAssigneeLog[];
  salesRepName?: string;
  loading?: boolean;
}

export const ViewAssigneeLogsModal: React.FC<ViewAssigneeLogsModalProps> = ({
  isOpen,
  onClose,
  logs,
  salesRepName,
  loading = false,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">
          &#8203;
        </span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <div>
              <h3 className="text-md font-medium text-gray-900">
                Assignee History:{" "}
                <span className="font-bold">{salesRepName}</span>
              </h3>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : logs && logs.length > 0 ? (
              <div className="space-y-4">
                {logs.map((log, index) => (
                  <div
                    key={index}
                    className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                  >
                    <div className="flex items-center text-sm text-gray-500 mb-3">
                      <Calendar className="w-4 h-4 mr-1" />
                      {formatDate(log.timestamp)}
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex-1 text-center">
                        <div className="text-xs text-gray-500 mb-1">
                          Previous Assignee
                        </div>
                        <div className="flex items-center justify-center">
                          <User className="w-4 h-4 mr-1 text-gray-400" />
                          <span className="text-sm font-medium text-gray-700">
                            {log.oldValue?.username}
                          </span>
                        </div>
                      </div>

                      <div className="px-4">
                        <ArrowRight className="w-5 h-5 text-gray-400" />
                      </div>

                      <div className="flex-1 text-center">
                        <div className="text-xs text-gray-500 mb-1">
                          New Assignee
                        </div>
                        <div className="flex items-center justify-center">
                          <User className="w-4 h-4 mr-1 text-green-500" />
                          <span className="text-sm font-medium text-gray-700">
                            {log.newValue?.username}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-500 text-center">
                      Updated by: {log.updatedBy?.username}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <User className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500 text-sm">
                  No logs found for this sales representative
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
            <Button
              type={ButtonType.Cancel}
              onClick={onClose}
              className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm"
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
